import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, DollarSign, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '@/contexts/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InvoicesPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const { formatDate } = useSettings();
  const [invoices, setInvoices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    booking_id: '',
    amount: '',
    due_date: '',
    description: '',
    line_items: []
  });
  const [lineItem, setLineItem] = useState({ description: '', amount: '' });

  useEffect(() => {
    fetchInvoices();
    fetchBookings();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const handleCreate = async () => {
    if (!newInvoice.booking_id || !newInvoice.amount) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await axios.post(`${API}/invoices`, newInvoice);
      toast.success('Invoice created successfully');
      setShowDialog(false);
      setNewInvoice({
        booking_id: '',
        amount: '',
        due_date: '',
        description: '',
        line_items: []
      });
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const addLineItem = () => {
    if (lineItem.description && lineItem.amount) {
      setNewInvoice({
        ...newInvoice,
        line_items: [...newInvoice.line_items, { ...lineItem }]
      });
      setLineItem({ description: '', amount: '' });
    }
  };

  const removeLineItem = (index) => {
    setNewInvoice({
      ...newInvoice,
      line_items: newInvoice.line_items.filter((_, i) => i !== index)
    });
  };

  const getBookingInfo = (bookingId) => {
    return bookings.find(b => b.id === bookingId);
  };

  const downloadPdf = async (invoiceId, invoiceNumber) => {
    setDownloadingPdf(invoiceId);
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`);
      
      // Convert base64 to blob
      const byteCharacters = atob(response.data.pdf_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename || `invoice_${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
      console.error('PDF download error:', error);
    } finally {
      setDownloadingPdf(null);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo}>
      <div data-testid="invoices-page">
        <div className="p-4 md:p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2" data-testid="invoices-heading">Invoices</h1>
              <p className="text-muted-foreground">Manage payments and invoices</p>
            </div>
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button className="bg-neon-blue hover:bg-neon-blue/80 text-black" data-testid="create-invoice-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="glass-card border-glass-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-invoice-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">Create New Invoice</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Select Booking *</Label>
                    <Select value={newInvoice.booking_id} onValueChange={(v) => setNewInvoice({ ...newInvoice, booking_id: v })}>
                      <SelectTrigger className="bg-muted/20 border-glass-border" data-testid="invoice-booking-select">
                        <SelectValue placeholder="Choose a booking..." />
                      </SelectTrigger>
                      <SelectContent className="bg-obsidian border-glass-border">
                        {bookings.map((booking) => (
                          <SelectItem key={booking.id} value={booking.id}>
                            {booking.client_name} - ${booking.total_amount}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount ($) *</Label>
                      <Input
                        type="number"
                        value={newInvoice.amount}
                        onChange={(e) => setNewInvoice({ ...newInvoice, amount: e.target.value })}
                        className="bg-muted/20 border-glass-border"
                        data-testid="invoice-amount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Due Date *</Label>
                      <Input
                        type="date"
                        value={newInvoice.due_date}
                        onChange={(e) => setNewInvoice({ ...newInvoice, due_date: e.target.value })}
                        className="bg-muted/20 border-glass-border"
                        data-testid="invoice-due-date-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={newInvoice.description}
                      onChange={(e) => setNewInvoice({ ...newInvoice, description: e.target.value })}
                      placeholder="e.g., Deposit for DJ services"
                      className="bg-muted/20 border-glass-border"
                      data-testid="invoice-description-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Line Items</Label>
                    <div className="flex gap-2">
                      <Input
                        value={lineItem.description}
                        onChange={(e) => setLineItem({ ...lineItem, description: e.target.value })}
                        placeholder="Description"
                        className="bg-muted/20 border-glass-border flex-1"
                        data-testid="line-item-description-input"
                      />
                      <Input
                        type="number"
                        value={lineItem.amount}
                        onChange={(e) => setLineItem({ ...lineItem, amount: e.target.value })}
                        placeholder="Amount"
                        className="bg-muted/20 border-glass-border w-32"
                        data-testid="line-item-amount-input"
                      />
                      <Button onClick={addLineItem} type="button" data-testid="add-line-item-btn">
                        Add
                      </Button>
                    </div>
                    <div className="space-y-2 mt-2">
                      {newInvoice.line_items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between bg-muted/20 p-2 rounded-lg" data-testid={`line-item-${index}`}>
                          <span>{item.description}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-neon-blue">${item.amount}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeLineItem(index)}
                              className="text-red-500 hover:text-red-400"
                              data-testid={`remove-line-item-btn-${index}`}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={handleCreate}
                    className="w-full bg-neon-blue hover:bg-neon-blue/80 text-black"
                    data-testid="invoice-create-submit-btn"
                  >
                    Create Invoice
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-3xl" data-testid="no-invoices-message">
              <p className="text-muted-foreground">No invoices yet. Create your first invoice!</p>
            </div>
          ) : (
            <div className="grid gap-4" data-testid="invoices-list">
              {invoices.map((invoice) => {
                const booking = getBookingInfo(invoice.booking_id);
                return (
                  <div
                    key={invoice.id}
                    className="glass-card-hover rounded-2xl p-6"
                    data-testid={`invoice-item-${invoice.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-heading text-xl font-semibold mb-1">
                          {invoice.invoice_number}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {booking ? booking.client_name : 'Unknown Client'}
                        </p>
                        {invoice.description && (
                          <p className="text-xs text-muted-foreground mt-1">{invoice.description}</p>
                        )}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        invoice.status === 'paid' ? 'bg-green-500/20 text-green-500' :
                        invoice.status === 'pending' ? 'bg-neon-blue/20 text-neon-blue' :
                        'bg-red-500/20 text-red-500'
                      }`}>
                        {invoice.status}
                      </span>
                    </div>
                    
                    {/* Line Items */}
                    {invoice.line_items && invoice.line_items.length > 0 && (
                      <div className="mb-4 border-t border-glass-border pt-4">
                        <span className="block text-xs text-muted-foreground mb-2">Line Items</span>
                        <div className="space-y-1">
                          {invoice.line_items.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-foreground/80">{item.description}</span>
                              <span className="text-foreground font-medium">${item.amount}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-3 gap-4 text-sm border-t border-glass-border pt-4">
                      <div>
                        <span className="block text-xs text-muted-foreground">Total Amount</span>
                        <span className="text-foreground font-semibold text-lg text-neon-blue">${invoice.amount}</span>
                      </div>
                      <div>
                        <span className="block text-xs text-muted-foreground">Paid</span>
                        <span className="text-foreground font-medium">${invoice.paid_amount || 0}</span>
                      </div>
                      <div>
                        <span className="block text-xs text-muted-foreground">Due Date</span>
                        <span className="text-foreground">{formatDate(invoice.due_date)}</span>
                      </div>
                    </div>
                    
                    {/* Download PDF Button */}
                    <div className="mt-4 pt-4 border-t border-glass-border">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadPdf(invoice.id, invoice.invoice_number)}
                        disabled={downloadingPdf === invoice.id}
                        className="border-border text-foreground hover:bg-muted"
                        data-testid={`download-pdf-btn-${invoice.id}`}
                      >
                        {downloadingPdf === invoice.id ? (
                          <>Generating...</>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}